import { OnInit, Component, EventEmitter, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { TrucksService, Truck, TankerData } from 'src/app/transportation/trucks/trucks.service';
import { KeycloakService } from 'src/app/authentication/keycloak.service';
import { forkJoin } from 'rxjs';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import _ from 'lodash';
import { Router } from '@angular/router';
import { PgTitleComponent, PgToolbarComponent } from '../../../shared/components/title-bar/title-bar.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { AuthenticationModule } from 'src/app/authentication/authentication.module';
import { AgGridModule } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { AgGridService } from 'src/app/shared/ag-grid/ag-grid.service';
import { TruckToolbarComponent } from '../trucks-toolbar/truck-toolbar/truck-toolbar.component';
import saveAs from 'file-saver';
import { UserLocalStoreService } from 'src/app/shared/services/user-local-store.service';

@Component({
  templateUrl: 'trucks-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, AuthenticationModule, AgGridModule],
})
export class TrucksListComponent implements OnInit, PgTitleComponent, PgToolbarComponent {

  title = new EventEmitter<string>();
  toolbarComponentType = TruckToolbarComponent;
  toolbarComponent: any;

  trucks!: Truck[];
  list: any[] = [];

  activeTrucks!: any[];

  _organisationsCache: {
    [key: string]: Organisation;
  } = {};
  _tankerCache: {
    [key: string]: TankerData;
  } = {};
  today!: number;

  gridOptions: GridOptions = {};
  activeGridOptions: GridOptions = {};
  gridRestored: boolean = false;

  constructor(private _trucksService: TrucksService,
    private _keycloakService: KeycloakService,
    private _organisationService: OrganisationService,
    private _router: Router,
    private _renderer: Renderer2,
    private _userLocalStorage: UserLocalStoreService) { }

  columnDefs = [
    {
      headerName: 'شماره پلاک', field: 'formattedRegistrationNumber',
      cellRenderer: (params:ICellRendererParams) => {
        const registrationNumber = (params.data.formattedRegistrationNumber) ? params.data.formattedRegistrationNumber : 'Edit';
        return AgGridService.editButtonWithText(
          this._renderer,
          this._router,
          (_router, data) => this.editRecord(data, 'formattedRegistrationNumber'),
          params.data,
          registrationNumber
        );
      }
    },

    {
      headerName: 'شماره مخزن', field: 'tanker.number',
      cellRenderer: (params:ICellRendererParams) => {
        const tankerNumber = (params.data.tanker) ? params.data.tanker.number : 'Edit';
        return AgGridService.editButtonWithText(
          this._renderer,
          this._router,
          (_router, data) => this.editRecord(data, 'tanker'),
          params.data,
          tankerNumber
        );
      },
    },

    {
      headerName: 'نام راننده',
      field: 'driver',
      cellRenderer: (params:ICellRendererParams) => {
        const driveName = (params.data.driver) ? params.data.driver : 'Edit';
        return AgGridService.editButtonWithText(
          this._renderer,
          this._router,
          (_router, data) => this.editRecord(data, 'driver'),
          params.data,
          driveName
        );
      }
    },

    {
      headerName: 'شماره ردیاب',
      field: 'imei',
      cellRenderer: (params:ICellRendererParams) => {
        const trackerNumber = (params.data.imei) ? params.data.imei : 'Edit';
        return AgGridService.editButtonWithText(
          this._renderer,
          this._router,
          (_router, data) => this.editRecord(data, 'tracker'),
          params.data,
          trackerNumber
        );
      }
    },

    {
      headerName: 'شرکت حمل و نقل',
      field: 'organisation',
      cellRenderer: (params:ICellRendererParams) => {
        const organisationName = (params.data.organisation) ? params.data.organisation : 'Edit';
        return AgGridService.editButtonWithText(
          this._renderer,
          this._router,
          (_router, data) => this.editRecord(data, 'transporter'),
          params.data,
          organisationName
        );
      }
    },

    {
      headerName: 'سند',
      field: 'fileId',
      cellRenderer: (params:ICellRendererParams) =>
        AgGridService.editButton(
          this._renderer,
          this._router,
          (_router, data) => this.editRecord(data, 'file'),
          params.data,
        )
    },

    { headerName: 'وضعیت', field: 'active' },
  ];

  activeColumnDefs = [
    {
      headerName: 'شرکت حمل و نقل', field: 'transporterOrganisationName',
      cellRenderer: (params: any) => this.formatOrganisation(params.value)
    },
    { headerName: 'تعداد کل کشنده ها', field: 'countOfTrucks' },
    { headerName: 'تعداد فعال', field: 'countOfActiveTrucks' }
  ];

  ngOnInit(): void {
    this.today = new Date().getTime();
    this.title.emit('Trucks');
    this.toolbarComponent.url = 'trucks/truck';
    this.toolbarComponent.excelExporting = this.exportExcel;

    this.getData();
    this.gridOptions = AgGridService.getOptions();
  }

  gridSetting(event: GridReadyEvent) {
    AgGridService.setting(event);
  }

  saveGridState(event: GridReadyEvent) {
    AgGridService.saveGridState(event, this.gridRestored, this._userLocalStorage, 'trucksGrid');
  }

  restoreGridState(event: GridReadyEvent) {
    this.gridRestored = AgGridService.restoreGridState(event, this._userLocalStorage, 'trucksGrid');
  }

  getData() {
    forkJoin([
      this._organisationService.get(),
      this._trucksService.getForLpgDist(),
      this._trucksService.getAllTankers()
    ]).subscribe(result => {
      result[0].forEach(o => {
        this._organisationsCache[o.name] = o;
      });
      this.trucks = result[1];
      result[2].forEach(t => {
        this._tankerCache[t.number + t.owner] = t;
      });
      this.list = this.mapTrucks();
      this.reportActivatedTrucks();
    });
  }

  hasRole(roleName: string) {
    return this._keycloakService.hasRole(roleName);
  }

  formatOrganisation(organisationId: string) {
    const foundOrganisation = this._organisationsCache[organisationId];
    if (foundOrganisation) {
      return foundOrganisation.nameTranslations['fa'];
    } else {
      return '';
    }
  }

  editRecord(truck: Truck, field: string) {
    this._router.navigate(['/trucks/truck', truck.formattedRegistrationNumber, field]);
  }

  addRecord() {
    this._router.navigate(['/trucks/truck']);
  }

  exportExcel(truckService: TrucksService) {
    truckService.getAllTankersExcel().subscribe(file => {
      saveAs(file.body!, 'tankers.xlsx');
    });
  }

  reportActivatedTrucks() {
    const grouped = _.groupBy(this.trucks, (item) => item.organisationId!.associatedObject);
    this.activeTrucks = _(grouped).map((items, key) => (
      {
        transporterOrganisationName: key,
        countOfTrucks: _.size(items),
        countOfActiveTrucks: _.size(_.filter(items, i => i.active))
      }
    )).value();
  }

  findTanker(truck: Truck) {
    if (truck.tanker !== undefined) {
      const t = this._tankerCache[truck.tanker!.associatedObject.number + truck.tanker!.associatedObject.owner];
      if (t) {
        return {
          number: truck.tanker!.associatedObject.number,
          licenseExpireDate: t.licenseExpireDate < this.today
        };
      }
    }
  }

  mapTrucks() {
    return this.trucks.map(truck => {
      const driverName = (truck.driver !== undefined) ? truck.driver!.associatedObject.name : null;
      const selectedImei = (truck.tracker) ? truck.tracker.associatedObject.imei : null;
      return {
        formattedRegistrationNumber: truck.formattedRegistrationNumber,
        tanker: this.findTanker(truck),
        driver: driverName,
        imei: selectedImei,
        organisation: (truck.organisationId) ? this.formatOrganisation(truck.organisationId.associatedObject) : null,
        fileId: truck.fileId,
        active: truck.active
      };
    });
  }

}
